<template>
  <el-container class="admin-shell" :style="{ '--sidebar-width': collapsed ? '64px' : '224px' }">
    <el-aside :width="collapsed ? '64px' : '224px'" class="sidebar">
      <div class="sidebar-brand">
        <img class="brand-mark small" :src="logo" alt="佐成乐赚" />
        <div v-show="!collapsed">
          <small>佐成乐赚</small>
        </div>
      </div>

      <el-menu :default-active="activeMenu" :collapse="collapsed" @select="onSelect">
        <template v-for="group in menuGroups" :key="group.label">
          <div v-show="!collapsed" class="menu-group-label">{{ group.label }}</div>
          <el-menu-item v-for="item in group.items" :key="item.key" :index="item.key">
            <el-icon><component :is="item.icon" /></el-icon>
            <span>{{ item.label }}</span>
          </el-menu-item>
        </template>
      </el-menu>

      <div class="user-box">
        <el-avatar :size="34">{{ admin.adminName.value.slice(0, 1) }}</el-avatar>
        <div v-show="!collapsed">
          <strong>{{ admin.adminName.value }}</strong>
          <small>超级管理员</small>
        </div>
        <el-tooltip content="退出登录" placement="top">
          <el-button text circle @click="onLogout">
            <el-icon><SwitchButton /></el-icon>
          </el-button>
        </el-tooltip>
      </div>
    </el-aside>

    <el-container>
      <el-header class="topbar">
        <div>
          <h1>{{ currentTitle }}</h1>
          <p>{{ currentDesc }} · {{ today }}</p>
        </div>
        <div class="topbar-actions">
          <el-button text @click="collapsed = !collapsed">
            <el-icon><Expand v-if="collapsed" /><Fold v-else /></el-icon>
          </el-button>
          <el-button type="primary" plain :loading="admin.loading.value" @click="admin.loadAll(true)">
            <el-icon><Refresh /></el-icon>刷新数据
          </el-button>
        </div>
      </el-header>

      <el-main class="main-content" v-loading="admin.loading.value">
        <component
          :is="currentView"
          :key="activeMenu"
          :admin="admin"
          @navigate="router.push('/admin/' + $event)"
        />
      </el-main>
    </el-container>
  </el-container>
</template>

<script setup>
import { computed, inject, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import {
  User, Grid, Iphone, Tickets, Warning, Refresh, SwitchButton, Fold, Expand, Odometer, DocumentCopy, Wallet, Key, CollectionTag
} from '@element-plus/icons-vue'
import logo from '../assets/logo.png'
import DashboardView from '../views/DashboardView.vue'
import CustomersView from '../views/CustomersView.vue'
import AppsView from '../views/AppsView.vue'
import DevicesView from '../views/DevicesView.vue'
import RecordsView from '../views/RecordsView.vue'
import RiskView from '../views/RiskView.vue'
import RiskConfigView from '../views/RiskConfigView.vue'
import RiskTagsView from '../views/RiskTagsView.vue'
import AdminLogsView from '../views/AdminLogsView.vue'
import BalanceLogView from '../views/BalanceLogView.vue'

const admin = inject('admin')
const route = useRoute()
const router = useRouter()

const collapsed = ref(false)

const menuGroups = [
  {
    label: '概览',
    items: [
      { key: 'dashboard', label: '数据大屏', icon: Odometer, desc: '核心指标与运营概览' }
    ]
  },
  {
    label: '业务',
    items: [
      { key: 'customers', label: '客户管理', icon: User, desc: '账户、额度与投放状态' },
      { key: 'apps', label: '应用配置', icon: Grid, desc: '应用与广告位参数' },
      { key: 'devices', label: '设备分组', icon: Iphone, desc: '设备清单与分组管理' },
      { key: 'records', label: '广告记录', icon: Tickets, desc: '投放记录与收益明细' },
      { key: 'balance-log', label: '额度流水', icon: Wallet, desc: '充值调账与消耗记录' },
      { key: 'risk-config', label: '风控配置', icon: Key, desc: '多公司阿里云 AccessKey' },
      { key: 'risk-tags', label: '风险列表', icon: CollectionTag, desc: '设备风险标签字典' }
    ]
  },
  {
    label: '审计',
    items: [
      { key: 'risk', label: '风控记录', icon: Warning, desc: '风控校验与异常追踪' },
      { key: 'admin-logs', label: '操作日志', icon: DocumentCopy, desc: '管理员操作留痕' }
    ]
  }
]

const menus = menuGroups.flatMap((group) => group.items)

const views = {
  dashboard: DashboardView,
  customers: CustomersView,
  apps: AppsView,
  devices: DevicesView,
  records: RecordsView,
  'balance-log': BalanceLogView,
  'risk-config': RiskConfigView,
  'risk-tags': RiskTagsView,
  risk: RiskView,
  'admin-logs': AdminLogsView
}

const activeMenu = computed(() => route.params.page || 'dashboard')
const currentMenu = computed(() => menus.find((item) => item.key === activeMenu.value))
const currentTitle = computed(() => currentMenu.value?.label || '管理后台')
const currentDesc = computed(() => currentMenu.value?.desc || '运营管理')
const currentView = computed(() => views[activeMenu.value] || DashboardView)

const onSelect = (key) => router.push(`/admin/${key}`)
const onLogout = () => {
  admin.logout()
  router.push({ name: 'admin-login' })
}

const today = new Intl.DateTimeFormat('zh-CN', {
  year: 'numeric', month: 'long', day: 'numeric', weekday: 'long'
}).format(new Date())
</script>
